/**
 * Step 2 — Modo de ejecución + cadencia.
 *
 * Captura: `methodology` (el modo de ejecución, Doc 06 §6; 4 opciones) + sprint length / start day / release
 * cadence. La cadencia aplica a fases agile y va en metadata del proyecto.
 *
 * `hybrid` se marca como recomendada (badge). Selección controlada por
 * cards clicable + radio semántico.
 */

import { useTranslation } from 'react-i18next'
import {
  GitCommitVertical,
  Repeat,
  Layers,
  GitFork,
  type LucideIcon,
} from 'lucide-react'

import type { ProjectMethodology } from '../../../api/projects'
import type {
  ReleaseCadence,
  SprintLengthWeeks,
  SprintStartDay,
  WizardState,
} from './types'

interface Step2Props {
  state: WizardState
  onPatch: (
    patch: Partial<
      Pick<
        WizardState,
        'methodology' | 'sprintLength' | 'sprintStart' | 'releaseCadence'
      >
    >,
  ) => void
}

interface MethodologyCard {
  key: ProjectMethodology
  icon: LucideIcon
  recommended: boolean
}

const METHODOLOGY_CARDS: MethodologyCard[] = [
  { key: 'waterfall', icon: GitCommitVertical, recommended: false },
  { key: 'scrum', icon: Repeat, recommended: false },
  { key: 'kanban', icon: Layers, recommended: false },
  { key: 'hybrid', icon: GitFork, recommended: true },
]

export function Step2Methodology({ state, onPatch }: Step2Props) {
  const { t } = useTranslation('projects')

  return (
    <div className="p-6">
      <h3 className="font-display text-lg font-semibold mb-1 text-ink">
        {t('wizard.step2.title')}
      </h3>
      <p className="text-sm text-ink-muted font-body mb-3">
        {t('wizard.step2.subtitle')}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
        {METHODOLOGY_CARDS.map((card) => (
          <MethodologyOption
            key={card.key}
            card={card}
            selected={state.methodology === card.key}
            onSelect={() => onPatch({ methodology: card.key })}
          />
        ))}
      </div>

      <div className="border-t border-line pt-5">
        <p className="text-xs font-medium text-ink-muted mb-3">
          {t('wizard.step2.cadence_title')}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <label className="block">
            <span className="text-xs text-ink-muted">
              {t('wizard.step2.cadence.sprint_length')}
            </span>
            <select
              value={String(state.sprintLength)}
              onChange={(e) =>
                onPatch({
                  sprintLength: Number(e.target.value) as SprintLengthWeeks,
                })
              }
              className="mt-1 w-full bg-canvas border border-line rounded-md px-3 py-2 text-sm"
            >
              <option value="1">{t('wizard.step2.cadence.weeks_one')}</option>
              <option value="2">{t('wizard.step2.cadence.weeks_two')}</option>
              <option value="3">{t('wizard.step2.cadence.weeks_three')}</option>
              <option value="4">{t('wizard.step2.cadence.weeks_four')}</option>
            </select>
          </label>
          <label className="block">
            <span className="text-xs text-ink-muted">
              {t('wizard.step2.cadence.sprint_start')}
            </span>
            <select
              value={state.sprintStart}
              onChange={(e) =>
                onPatch({ sprintStart: e.target.value as SprintStartDay })
              }
              className="mt-1 w-full bg-canvas border border-line rounded-md px-3 py-2 text-sm"
            >
              <option value="monday">{t('wizard.step2.cadence.monday')}</option>
              <option value="tuesday">
                {t('wizard.step2.cadence.tuesday')}
              </option>
              <option value="wednesday">
                {t('wizard.step2.cadence.wednesday')}
              </option>
            </select>
          </label>
          <label className="block">
            <span className="text-xs text-ink-muted">
              {t('wizard.step2.cadence.release_cadence')}
            </span>
            <select
              value={state.releaseCadence}
              onChange={(e) =>
                onPatch({ releaseCadence: e.target.value as ReleaseCadence })
              }
              className="mt-1 w-full bg-canvas border border-line rounded-md px-3 py-2 text-sm"
            >
              <option value="each_sprint">
                {t('wizard.step2.cadence.release_each_sprint')}
              </option>
              <option value="two_sprints">
                {t('wizard.step2.cadence.release_two_sprints')}
              </option>
              <option value="on_demand">
                {t('wizard.step2.cadence.release_on_demand')}
              </option>
            </select>
          </label>
        </div>
      </div>
    </div>
  )
}

function MethodologyOption({
  card,
  selected,
  onSelect,
}: {
  card: MethodologyCard
  selected: boolean
  onSelect: () => void
}) {
  const { t } = useTranslation('projects')
  const Icon = card.icon
  const baseClasses = selected
    ? 'border-2 border-brand bg-brand-soft/30 rounded-md p-4 text-left relative transition-colors'
    : 'border border-line rounded-md p-4 text-left hover:border-line-strong transition-colors relative'
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={baseClasses}
    >
      {card.recommended ? (
        <span className="absolute top-2 right-2 px-1.5 py-0.5 rounded text-[9px] font-medium uppercase tracking-wider bg-brand text-canvas">
          {t('wizard.step2.recommended_badge')}
        </span>
      ) : null}
      <div className="flex items-start gap-3">
        <Icon
          className={
            selected
              ? 'w-5 h-5 text-brand mt-0.5 shrink-0'
              : 'w-5 h-5 text-ink-muted mt-0.5 shrink-0'
          }
        />
        <div className="flex-1">
          <p className="text-sm font-semibold text-ink">
            {t(`methodology.${card.key}` as const)}
          </p>
          <p className="text-xs text-ink-muted mt-1 font-body">
            {t(`wizard.step2.options.${card.key}.description` as const)}
          </p>
          <p className="text-[10px] text-ink-subtle mt-2">
            {t(`wizard.step2.options.${card.key}.hint` as const)}
          </p>
        </div>
      </div>
    </button>
  )
}
